import { Box, Typography } from "@mui/material";
import { TagManager } from "components/TagManager";
import {
  useAddOrganizationTagMutation,
  useAddTagMutation,
  useDeleteOrganizationTagByOrgIdAndTagIdMutation,
  useGetTagsByTypeQuery,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { FC } from "react";
import { TAG_TYPE_DOMAIN, Tag } from "types/tag";
import { ROLES } from "types/user";

type DomainTagsProps = {
  organizationId: string;
  tags: Tag[];
  isEditing?: boolean;
  onChanged?: () => void;
};

export const DomainTags: FC<DomainTagsProps> = ({
  organizationId,
  tags,
  isEditing = true,
  onChanged,
}) => {
  const { user } = useAuthUser();
  const canCreateDomain =
    user?.role === ROLES.ADMIN || user?.role === ROLES.DIRECTOR;

  const [addTag] = useAddTagMutation();
  const [addOrganizationTag] = useAddOrganizationTagMutation();
  const [deleteOrganizationTag] =
    useDeleteOrganizationTagByOrgIdAndTagIdMutation();
  const { data: domainTagsData, refetch: refetchDomainTags } =
    useGetTagsByTypeQuery({
      variables: { type: TAG_TYPE_DOMAIN },
      skip: !canCreateDomain,
    });

  const resolveTagId = async (tag: Tag): Promise<string | undefined> => {
    if (tag.tag_id) return tag.tag_id;

    const name = tag.name.trim();
    if (!name || !canCreateDomain) return undefined;

    const existing = (domainTagsData?.tag as Tag[] | undefined)?.find(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing?.tag_id) return existing.tag_id;

    const created = await addTag({
      variables: { name, type: TAG_TYPE_DOMAIN },
    });
    const returnedId = created.data?.insert_tag?.returning?.[0]?.tag_id;
    if (returnedId) return returnedId;

    const refreshed = await refetchDomainTags();
    return (refreshed.data?.tag as Tag[] | undefined)?.find(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    )?.tag_id;
  };

  const handleAdd = async (tag: Tag) => {
    const tagId = await resolveTagId(tag);
    if (!tagId) return;
    await addOrganizationTag({
      variables: { org_id: organizationId, tag_id: tagId },
    });
    onChanged?.();
  };

  const handleDelete = async (tag: Tag) => {
    if (!tag.tag_id) return;
    await deleteOrganizationTag({
      variables: { org_id: organizationId, tag_id: tag.tag_id },
    });
    onChanged?.();
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={400} mt={2} mb={1}>
        Domains
      </Typography>
      <TagManager
        tags={tags}
        isEditing={isEditing}
        tagType={TAG_TYPE_DOMAIN}
        allowCreate={canCreateDomain}
        placeholder={
          canCreateDomain ? "Select or create Domain" : "Select Domain"
        }
        onAdd={handleAdd}
        onDelete={handleDelete}
      />
    </Box>
  );
};
